// FeedbackModal.js - Main feedback modal with tabs for users
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import THEME from '../../styles/theme';
import api from '../../utils/api';
import RecentUpdates from './RecentUpdates';
import ReportForm from './ReportForm';
import MyRequests from './MyRequests';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${THEME.zIndex.modal};
  animation: ${fadeIn} ${THEME.transitionFast};
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: 16px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} ${THEME.transitionMedium};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${THEME.spacing.lg} ${THEME.spacing.xl};
  border-bottom: 1px solid ${THEME.inputBorder};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${THEME.fontSize.xl};
  color: ${THEME.textPrimary};
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xl};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${THEME.textPrimary};
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${THEME.inputBorder};
  padding: 0 ${THEME.spacing.lg};
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: ${THEME.spacing.md} ${THEME.spacing.lg};
  color: ${props => props.$active ? THEME.textPrimary : THEME.textSecondary};
  font-size: ${THEME.fontSize.md};
  cursor: pointer;
  position: relative;
  transition: color ${THEME.transitionFast};

  &:hover {
    color: ${THEME.textPrimary};
  }

  ${props => props.$active && `
    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: ${THEME.accentPrimary};
    }
  `}
`;

const TabBadge = styled.span`
  background: ${THEME.accentPrimary};
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${THEME.spacing.lg};
`;

const FeedbackModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('updates');
  const [myRequests, setMyRequests] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('updates'); // Always default to Updates tab when opening
      loadData();
      // Mark as viewed when modal opens
      api.markFeedbackViewed().catch(console.error);
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [feedbackData, updatesData] = await Promise.all([
        api.getFeedback(),
        api.getRecentUpdates()
      ]);
      setMyRequests(feedbackData);
      setRecentUpdates(updatesData);

      // Count items with updates
      const withUpdates = feedbackData.filter(item => item.hasUpdate).length;
      setUpdateCount(withUpdates);
    } catch (error) {
      console.error('Failed to load feedback data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSuccess = () => {
    loadData();
    setActiveTab('requests');
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Updates/Feedback</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close">
            ✕
          </CloseButton>
        </ModalHeader>

        <TabContainer>
          <Tab
            $active={activeTab === 'updates'}
            onClick={() => setActiveTab('updates')}
          >
            Recent Updates
          </Tab>
          <Tab
            $active={activeTab === 'bug'}
            onClick={() => setActiveTab('bug')}
          >
            Report Bug
          </Tab>
          <Tab
            $active={activeTab === 'feature'}
            onClick={() => setActiveTab('feature')}
          >
            Request Feature
          </Tab>
          <Tab
            $active={activeTab === 'requests'}
            onClick={() => setActiveTab('requests')}
          >
            My Requests
            {updateCount > 0 && <TabBadge>{updateCount}</TabBadge>}
          </Tab>
        </TabContainer>

        <ModalBody>
          {activeTab === 'updates' && (
            <RecentUpdates updates={recentUpdates} loading={loading} />
          )}
          {activeTab === 'bug' && (
            <ReportForm type="bug" onSuccess={handleSubmitSuccess} />
          )}
          {activeTab === 'feature' && (
            <ReportForm type="feature" onSuccess={handleSubmitSuccess} />
          )}
          {activeTab === 'requests' && (
            <MyRequests requests={myRequests} loading={loading} onRefresh={loadData} />
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default FeedbackModal;
